import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CookieBanner } from "@/components/CookieBanner";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { useMaintenanceCheck } from "@/hooks/useMaintenanceCheck";
import GeoRestrictedLayout from "@/components/GeoRestrictedLayout";
import Index from "./pages/Index";
import LandingOption1 from "./pages/LandingOption1";
import LandingOption2 from "./pages/LandingOption2";
import LandingOption3 from "./pages/LandingOption3";
import LandingOption4 from "./pages/LandingOption4";
import LandingOption5 from "./pages/LandingOption5";
import Auth from "./pages/Auth";
import Onboarding from "./pages/Onboarding";
import About from "./pages/About";
import Features from "./pages/Features";
import Contact from "./pages/Contact";
import Dashboard from "./pages/Dashboard";
import ChairDashboard from "./pages/ChairDashboard";
import TreasurerDashboard from "./pages/TreasurerDashboard";
import SecretaryDashboard from "./pages/SecretaryDashboard";
import TrusteeDashboard from "./pages/TrusteeDashboard";
import AssetsDashboard from "./pages/AssetsDashboard";
import CateringDashboard from "./pages/CateringDashboard";
import EventsDashboard from "./pages/EventsDashboard";
import CommunicationsDashboard from "./pages/CommunicationsDashboard";
import CommunityDashboard from "./pages/CommunityDashboard";
import SchoolLiaisonDashboard from "./pages/SchoolLiaisonDashboard";
import ShopDashboard from "./pages/ShopDashboard";
import Settings from "./pages/Settings";
import Library from "./pages/Library";
import Forum from "./pages/Forum";
import Shop from "./pages/Shop";
import Marketplace from "./pages/Marketplace";
import CreateListing from "./pages/CreateListing";
import BuyCredits from "./pages/BuyCredits";
import MyListings from "./pages/MyListings";
import FundraisingPage from "./pages/FundraisingPage";
import Documents from "./pages/Documents";
import Members from "./pages/Members";
import Pricing from "./pages/Pricing";
import PricingNew from "./pages/PricingNew";
import Support from "./pages/Support";
import SupportArticle from "./pages/SupportArticle";
import Games from "./pages/Games";
import RaffleGame from "./pages/RaffleGame";
import GuessingGame from "./pages/GuessingGame";
import BingoGame from "./pages/BingoGame";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Cookies from "./pages/Cookies";
import Refund from "./pages/Refund";
import NotFound from "./pages/NotFound";
import Maintenance from "./pages/Maintenance";
import AdminLogin from "./pages/AdminLogin";
import PTAAdmin from "./pages/PTAAdmin";
import SiteAdmin from "./pages/SiteAdmin";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import StaffSubmission from "./pages/StaffSubmission";
import Changelog from "./pages/Changelog";
import ParentPortal from "./pages/ParentPortal";
import ParentProfile from "./pages/ParentProfile";
import Sponsorship from "./pages/Sponsorship";
import Roadmap from "./pages/Roadmap";
import Integrations from "./pages/Integrations";
import Partners from "./pages/Partners";
import StallApplication from "./pages/StallApplication";
import TrialExpired from "./pages/TrialExpired";
import AcceptInvitation from "./pages/AcceptInvitation";
import BecomeAMember from "./pages/BecomeAMember";
import LoanPayment from "./pages/LoanPayment";
import StallPayment from "./pages/StallPayment";
import PaymentSuccess from "./pages/PaymentSuccess";
import VolunteerSignup from "./pages/VolunteerSignup";
import EventCheckin from "./pages/EventCheckin";
import ResetPassword from "./pages/ResetPassword";
import VerifyEmail from "./pages/VerifyEmail";
import VerifyParent from "./pages/VerifyParent";
import CheckEmail from "./pages/CheckEmail";
import CheckParentEmail from "./pages/CheckParentEmail";

const queryClient = new QueryClient();

const Layout = ({ children }: { children: React.ReactNode }) => (
  <div className="flex min-h-screen flex-col">
    <Header />
    <main className="flex-1 container py-6">
      {children}
    </main>
    <Footer />
    <CookieBanner />
  </div>
);

const App = () => {
  const { showMaintenance, loading } = useMaintenanceCheck();

  if (loading) {
    return null; // or a loading spinner
  }

  if (showMaintenance) {
    return (
      <QueryClientProvider client={queryClient}>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Routes>
            <Route path="/admin-login" element={<AdminLogin />} />
            {/* Site admin routes available during maintenance */}
            <Route path="/dashboard" element={<ProtectedRoute><Layout><Dashboard /></Layout></ProtectedRoute>} />
            <Route path="/dashboard/chair" element={<ProtectedRoute><Layout><ChairDashboard /></Layout></ProtectedRoute>} />
            <Route path="/dashboard/treasurer" element={<ProtectedRoute><Layout><TreasurerDashboard /></Layout></ProtectedRoute>} />
            <Route path="/dashboard/secretary" element={<ProtectedRoute><Layout><SecretaryDashboard /></Layout></ProtectedRoute>} />
            <Route path="/dashboard/trustee" element={<ProtectedRoute><Layout><TrusteeDashboard /></Layout></ProtectedRoute>} />
            <Route path="/dashboard/assets" element={<ProtectedRoute><Layout><AssetsDashboard /></Layout></ProtectedRoute>} />
            <Route path="/dashboard/catering" element={<ProtectedRoute><Layout><CateringDashboard /></Layout></ProtectedRoute>} />
            <Route path="/dashboard/events" element={<ProtectedRoute><Layout><EventsDashboard /></Layout></ProtectedRoute>} />
            <Route path="/dashboard/communications" element={<ProtectedRoute><Layout><CommunicationsDashboard /></Layout></ProtectedRoute>} />
            <Route path="/dashboard/community" element={<ProtectedRoute><Layout><CommunityDashboard /></Layout></ProtectedRoute>} />
            <Route path="/dashboard/school-liaison" element={<ProtectedRoute><Layout><SchoolLiaisonDashboard /></Layout></ProtectedRoute>} />
            <Route path="/dashboard/shop" element={<ProtectedRoute><Layout><ShopDashboard /></Layout></ProtectedRoute>} />
            <Route path="/site-admin" element={<ProtectedRoute requireRole="site_admin"><Layout><SiteAdmin /></Layout></ProtectedRoute>} />
            <Route path="/pta-admin" element={<ProtectedRoute><Layout><PTAAdmin /></Layout></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><Layout><Settings /></Layout></ProtectedRoute>} />
            <Route path="/members" element={<ProtectedRoute><Layout><Members /></Layout></ProtectedRoute>} />
            <Route path="/documents" element={<ProtectedRoute><Layout><Documents /></Layout></ProtectedRoute>} />
            <Route path="/library" element={<ProtectedRoute><Layout><Library /></Layout></ProtectedRoute>} />
            <Route path="/forum" element={<ProtectedRoute><Layout><Forum /></Layout></ProtectedRoute>} />
            <Route path="/shop" element={<ProtectedRoute><Layout><Shop /></Layout></ProtectedRoute>} />
            <Route path="*" element={<Maintenance />} />
          </Routes>
        </BrowserRouter>
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <Routes>
        {/* Geo-restricted public marketing pages */}
        <Route path="/" element={<GeoRestrictedLayout><Index /></GeoRestrictedLayout>} />
        <Route path="/landing-option-1" element={<GeoRestrictedLayout><LandingOption1 /></GeoRestrictedLayout>} />
        <Route path="/landing-option-2" element={<GeoRestrictedLayout><LandingOption2 /></GeoRestrictedLayout>} />
        <Route path="/landing-option-3" element={<GeoRestrictedLayout><LandingOption3 /></GeoRestrictedLayout>} />
        <Route path="/landing-option-4" element={<GeoRestrictedLayout><LandingOption4 /></GeoRestrictedLayout>} />
        <Route path="/landing-option-5" element={<GeoRestrictedLayout><LandingOption5 /></GeoRestrictedLayout>} />
        <Route path="/about" element={<GeoRestrictedLayout><About /></GeoRestrictedLayout>} />
        <Route path="/features" element={<GeoRestrictedLayout><Features /></GeoRestrictedLayout>} />
        <Route path="/contact" element={<GeoRestrictedLayout><Contact /></GeoRestrictedLayout>} />
        
        {/* Auth pages - no geo-restriction */}
        <Route path="/auth" element={<Layout><Auth /></Layout>} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/verify-parent" element={<VerifyParent />} />
        <Route path="/check-email" element={<CheckEmail />} />
        <Route path="/check-parent-email" element={<CheckParentEmail />} />
        <Route path="/onboarding" element={
          <ProtectedRoute>
            <Onboarding />
          </ProtectedRoute>
        } />
        <Route path="/accept-invitation" element={<AcceptInvitation />} />
        <Route path="/dashboard" element={<ProtectedRoute><Layout><Dashboard /></Layout></ProtectedRoute>} />
        <Route path="/dashboard/chair" element={<ProtectedRoute><Layout><ChairDashboard /></Layout></ProtectedRoute>} />
        <Route path="/dashboard/treasurer" element={<ProtectedRoute><Layout><TreasurerDashboard /></Layout></ProtectedRoute>} />
        <Route path="/dashboard/secretary" element={<ProtectedRoute><Layout><SecretaryDashboard /></Layout></ProtectedRoute>} />
        <Route path="/dashboard/trustee" element={<ProtectedRoute><Layout><TrusteeDashboard /></Layout></ProtectedRoute>} />
        <Route path="/dashboard/assets" element={<ProtectedRoute><Layout><AssetsDashboard /></Layout></ProtectedRoute>} />
        <Route path="/dashboard/catering" element={<ProtectedRoute><Layout><CateringDashboard /></Layout></ProtectedRoute>} />
        <Route path="/dashboard/events" element={<ProtectedRoute><Layout><EventsDashboard /></Layout></ProtectedRoute>} />
        <Route path="/dashboard/communications" element={<ProtectedRoute><Layout><CommunicationsDashboard /></Layout></ProtectedRoute>} />
        <Route path="/dashboard/community" element={<ProtectedRoute><Layout><CommunityDashboard /></Layout></ProtectedRoute>} />
        <Route path="/dashboard/school-liaison" element={<ProtectedRoute><Layout><SchoolLiaisonDashboard /></Layout></ProtectedRoute>} />
        <Route path="/dashboard/shop" element={<ProtectedRoute><Layout><ShopDashboard /></Layout></ProtectedRoute>} />
        <Route path="/pta-admin" element={<ProtectedRoute><Layout><PTAAdmin /></Layout></ProtectedRoute>} />
        <Route path="/trial-expired" element={<ProtectedRoute><Layout><TrialExpired /></Layout></ProtectedRoute>} />
        <Route path="/site-admin" element={<ProtectedRoute requireRole="site_admin"><Layout><SiteAdmin /></Layout></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><Layout><Settings /></Layout></ProtectedRoute>} />
        <Route path="/library" element={<ProtectedRoute><Layout><Library /></Layout></ProtectedRoute>} />
        <Route path="/forum" element={<ProtectedRoute><Layout><Forum /></Layout></ProtectedRoute>} />
        <Route path="/shop" element={<ProtectedRoute><Layout><Shop /></Layout></ProtectedRoute>} />
        <Route path="/marketplace" element={<Marketplace />} />
        <Route path="/marketplace/create" element={<ProtectedRoute><CreateListing /></ProtectedRoute>} />
        <Route path="/marketplace/buy-credits" element={<ProtectedRoute><BuyCredits /></ProtectedRoute>} />
        <Route path="/marketplace/my-listings" element={<ProtectedRoute><MyListings /></ProtectedRoute>} />
        <Route path="/fundraising" element={<FundraisingPage />} />
        <Route path="/documents" element={<ProtectedRoute><Layout><Documents /></Layout></ProtectedRoute>} />
        <Route path="/members" element={<ProtectedRoute><Layout><Members /></Layout></ProtectedRoute>} />
        {/* Geo-restricted public pages */}
        <Route path="/pricing" element={<GeoRestrictedLayout><PricingNew /></GeoRestrictedLayout>} />
        <Route path="/pricing-old" element={<GeoRestrictedLayout><Pricing /></GeoRestrictedLayout>} />
        <Route path="/support" element={<GeoRestrictedLayout><Support /></GeoRestrictedLayout>} />
        <Route path="/support/:slug" element={<GeoRestrictedLayout><SupportArticle /></GeoRestrictedLayout>} />
        <Route path="/games" element={<GeoRestrictedLayout><Games /></GeoRestrictedLayout>} />
        <Route path="/games/raffle" element={<GeoRestrictedLayout><RaffleGame /></GeoRestrictedLayout>} />
        <Route path="/games/guessing" element={<GeoRestrictedLayout><GuessingGame /></GeoRestrictedLayout>} />
        <Route path="/games/bingo" element={<GeoRestrictedLayout><BingoGame /></GeoRestrictedLayout>} />
        <Route path="/blog" element={<GeoRestrictedLayout><Blog /></GeoRestrictedLayout>} />
        <Route path="/blog/:id" element={<GeoRestrictedLayout><BlogPost /></GeoRestrictedLayout>} />
        <Route path="/changelog" element={<GeoRestrictedLayout><Changelog /></GeoRestrictedLayout>} />
        <Route path="/roadmap" element={<GeoRestrictedLayout><Roadmap /></GeoRestrictedLayout>} />
        <Route path="/sponsorship" element={<GeoRestrictedLayout><Sponsorship /></GeoRestrictedLayout>} />
        <Route path="/integrations" element={<GeoRestrictedLayout><Integrations /></GeoRestrictedLayout>} />
        <Route path="/partners" element={<GeoRestrictedLayout><Partners /></GeoRestrictedLayout>} />
        
        {/* Legal pages - no geo-restriction */}
        <Route path="/privacy" element={<Layout><Privacy /></Layout>} />
        <Route path="/terms" element={<Layout><Terms /></Layout>} />
        <Route path="/cookies" element={<Layout><Cookies /></Layout>} />
        <Route path="/refund" element={<Layout><Refund /></Layout>} />
        
        {/* PTA-specific public pages - no geo-restriction (for existing PTAs) */}
        <Route path="/portal" element={<ParentPortal />} />
        <Route path="/portal/:ptaId" element={<ParentPortal />} />
        <Route path="/parent-profile" element={<ParentProfile />} />
        <Route path="/staff-request/:ptaId" element={<StaffSubmission />} />
        <Route path="/stall-application/:ptaId" element={<StallApplication />} />
        <Route path="/fundraising/:ptaId" element={<FundraisingPage />} />
        <Route path="/become-a-member/:ptaId" element={<BecomeAMember />} />
        <Route path="/loan-payment/:loanId" element={<LoanPayment />} />
        <Route path="/stall-payment/:applicationId" element={<StallPayment />} />
        <Route path="/payment-success" element={<PaymentSuccess />} />
        <Route path="/stalls/:ptaId" element={<StallApplication />} />
        <Route path="/v/:ptaId" element={<VolunteerSignup />} />
        <Route path="/event-checkin" element={<ProtectedRoute><EventCheckin /></ProtectedRoute>} />
        <Route path="/event-checkin/:eventId" element={<ProtectedRoute><EventCheckin /></ProtectedRoute>} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  </QueryClientProvider>
);
};

export default App;
